import { create } from 'zustand'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Default settings values
const DEFAULT_SETTINGS = {
  // Movement settings
  staticMode: false,         // When true, critters don't move (randomly placed but static)
  roamInterval: 3000,        // Pick new target every X ms
  roamSpeed: 0.15,           // Movement speed (% per update)
  updateInterval: 250,       // Position update frequency (ms)

  // Boundary settings (percentage-based)
  minX: 5,
  maxX: 85,
  minY: 0,
  maxY: 15,

  // Critter limits
  maxCritters: 20,
  defaultCritterType: 'ferret',

  // Animation durations (ms)
  feedDuration: 2000,
  petDuration: 500,
  chatBubbleDuration: 5000,
  spawnDuration: 500,
  despawnDuration: 500,

  // Subscription requirements
  requireSubscription: true,
  requiredTier: 3000,        // 1000 = Tier 1, 2000 = Tier 2, 3000 = Tier 3

  // Feature toggles
  allowChatBubbles: true,
  allowFeeding: true,
  allowPetting: true,
  allowDancing: true,
  allowTricks: true,
  allowHugging: true,
  allowBonking: true,
  allowCritterTypeChange: true,

  // Interaction durations (ms)
  danceDuration: 3000,
  trickDuration: 1500,
  hugDuration: 2500,
  bonkDuration: 1500,

  // Visual settings
  critterScale: 1.0,         // Multiplier for critter size
  showUsernames: true,

  // Animation settings
  enableAnimations: false,   // When true, use animated spritesheets instead of static images
  animationFps: 12,          // Frames per second for sprite animations

  // Sleep/wake settings
  enableSleepMode: false,     // When true, inactive critters fall asleep
  sleepTimeout: 300000,       // Time of inactivity before sleeping (ms) - default 5 minutes

  // 3D rendering settings
  enable3D: true,             // Global toggle for 3D model rendering
  threeDModelScale: 1.0,      // Global scale multiplier for 3D models

  // 3D movement settings (independent from 2D)
  threeDStaticMode: false,    // When true, 3D critters don't move
  threeDRoamInterval: 3000,   // How often 3D critters pick a new destination (ms)
  threeDRoamSpeed: 0.45,      // 3D movement speed (% per update) - faster default than 2D
  threeDUpdateInterval: 100,  // 3D position update frequency (ms) - smoother default
  threeDLerpSpeed: 12,        // Smooth interpolation speed for 3D models

  // Admin settings
  modCanUseAdminCommands: true,
  broadcasterOnlyAdmin: false
}

const useSettingsStore = create((set, get) => ({
  settings: { ...DEFAULT_SETTINGS },
  broadcasterLogin: null,
  isLoading: false,
  hasChanges: false,
  isPanelOpen: false,
  is3DPanelOpen: false,

  // Set broadcaster context
  setBroadcasterLogin: (login) => set({ broadcasterLogin: login }),

  // Toggle 2D settings panel
  togglePanel: () => set(state => ({ isPanelOpen: !state.isPanelOpen, is3DPanelOpen: false })),
  openPanel: () => set({ isPanelOpen: true, is3DPanelOpen: false }),
  closePanel: () => set({ isPanelOpen: false }),

  // Toggle 3D settings panel
  toggle3DPanel: () => set(state => ({ is3DPanelOpen: !state.is3DPanelOpen, isPanelOpen: false })),
  open3DPanel: () => set({ is3DPanelOpen: true, isPanelOpen: false }),
  close3DPanel: () => set({ is3DPanelOpen: false }),

  // Update a single setting
  updateSetting: (key, value) => {
    set(state => ({
      settings: { ...state.settings, [key]: value },
      hasChanges: true
    }))
  },

  // Update multiple settings at once
  updateSettings: (newSettings) => {
    set(state => ({
      settings: { ...state.settings, ...newSettings },
      hasChanges: true
    }))
  },

  // Reset to defaults
  resetToDefaults: () => {
    set({ settings: { ...DEFAULT_SETTINGS }, hasChanges: true })
  },

  // Load settings from API
  loadFromApi: async (broadcasterLogin) => {
    set({ isLoading: true, broadcasterLogin })
    try {
      const response = await fetch(`${API_BASE}/twitch/api/broadcaster/${broadcasterLogin}/settings`)
      if (response.ok) {
        const data = await response.json()
        if (data.settings) {
          set({
            settings: { ...DEFAULT_SETTINGS, ...data.settings },
            hasChanges: false
          })
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
    } finally {
      set({ isLoading: false })
    }
  },

  // Save settings to API
  saveToApi: async () => {
    const { broadcasterLogin, settings } = get()
    if (!broadcasterLogin) return false

    set({ isLoading: true })
    try {
      const response = await fetch(`${API_BASE}/twitch/api/broadcaster/${broadcasterLogin}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings })
      })
      if (response.ok) {
        set({ hasChanges: false })
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to save settings:', error)
      return false
    } finally {
      set({ isLoading: false })
    }
  },

  // Get a setting value (with default fallback)
  getSetting: (key) => {
    const { settings } = get()
    return settings[key] ?? DEFAULT_SETTINGS[key]
  }
}))

export { DEFAULT_SETTINGS }
export default useSettingsStore